import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "emailField", "submitButton", "usageText", "progressBar", "statusText"]

  connect() {
    console.log("Free emails controller connected")
    console.log("Available targets:", {
      form: this.hasFormTarget,
      emailField: this.hasEmailFieldTarget,
      submitButton: this.hasSubmitButtonTarget,
      usageText: this.hasUsageTextTarget,
      progressBar: this.hasProgressBarTarget,
      statusText: this.hasStatusTextTarget
    })
  }

  async submit(event) {
    event.preventDefault()
    event.stopPropagation()
    
    console.log("Submit handler triggered")
    
    const form = this.formTarget
    const email = this.emailFieldTarget.value.trim()
    
    if (!email) {
      this.showStatusText("error", "Please enter an email address")
      this.showNotification("Please enter an email address", "alert")
      return
    }
    
    console.log("Sending email to:", email)
    
    // Show sending state
    this.showStatusText("sending", "Sending Pitch...")
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add("opacity-75", "cursor-not-allowed")
    this.submitButtonTarget.classList.remove("hover:bg-primary-500")
    
    try {
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })
      
      console.log("Response status:", response.status)
      const data = await response.json()
      console.log("Response data:", data)
      
      if (response.ok && data.success) {
        // Show success state
        this.showStatusText("success", "✓ Pitch sent successfully!")
        this.showNotification(data.message, "notice")
        
        // Clear the email field
        this.emailFieldTarget.value = ""
        
        console.log("Updating stats - Used:", data.emails_used, "Limit:", data.monthly_limit)
        
        // Update usage statistics
        this.updateUsageStats(data.emails_used, data.monthly_limit)
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.clearStatusText()
        }, 3000)
      } else {
        // Show error state
        this.showStatusText("error", data.message || "Failed to send email")
        this.showNotification(data.message || "Failed to send email", "alert")
      }
    } catch (error) {
      console.error("Error sending free email:", error)
      this.showStatusText("error", "An error occurred. Please try again.")
      this.showNotification("An error occurred. Please try again.", "alert")
    } finally {
      // Re-enable submit button
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("opacity-75", "cursor-not-allowed")
      this.submitButtonTarget.classList.add("hover:bg-primary-500")
    }
  }

  showStatusText(type, message) {
    if (!this.hasStatusTextTarget) return
    
    const statusText = this.statusTextTarget
    
    // Set color based on type
    let colorClass = ""
    if (type === "sending") {
      colorClass = "text-blue-400"
    } else if (type === "success") {
      colorClass = "text-green-400"
    } else if (type === "error") {
      colorClass = "text-red-400"
    }
    
    // Remove previous color classes
    statusText.classList.remove("text-blue-400", "text-green-400", "text-red-400")
    
    // Add new color class
    statusText.classList.add(colorClass)
    
    // Update message
    statusText.textContent = message
    
    // Show the status text with fade in
    statusText.style.opacity = "1"
  }

  clearStatusText() {
    if (!this.hasStatusTextTarget) return
    
    const statusText = this.statusTextTarget
    
    // Fade out
    statusText.style.opacity = "0"
    
    // Clear text after fade
    setTimeout(() => {
      statusText.textContent = ""
    }, 300)
  }

  showNotification(message, type) {
    // Create a flash message element
    const flashContainer = document.getElementById("flash")
    if (!flashContainer) return
    
    const alertDiv = document.createElement("div")
    alertDiv.setAttribute("data-controller", "alert")
    alertDiv.setAttribute("data-transition-enter", "transition-all ease-linear duration-300")
    alertDiv.setAttribute("data-transition-enter-from", "opacity-0 h-0")
    alertDiv.setAttribute("data-transition-enter-to", "opacity-100")
    alertDiv.setAttribute("data-transition-leave", "transition-all ease-linear duration-300")
    alertDiv.setAttribute("data-transition-leave-from", "opacity-100")
    alertDiv.setAttribute("data-transition-leave-to", "opacity-0")
    
    if (type === "notice") {
      alertDiv.setAttribute("data-alert-dismiss-after-value", "8000")
    }
    
    const colorClass = type === "notice" 
      ? "border-slate-500 shadow-primary-900/80" 
      : "border-red-500 shadow-red-900/80"
    
    alertDiv.className = `absolute top-0 right-4 flex items-center px-3 py-2 shadow-lg rounded-md border bg-slate-950 ${colorClass}`
    alertDiv.setAttribute("role", "alert")
    
    // Create icon based on type
    const iconSvg = type === "notice"
      ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-primary-500 mr-2 size-5"><path stroke-linecap="round" stroke-linejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" /></svg>`
      : `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="text-red-500 mr-2 size-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>`
    
    alertDiv.innerHTML = `
      ${iconSvg}
      <p class="text-sm">${message}</p>
      <button type="button" data-action="alert#close" class="ml-2 hover:text-slate-500">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" /></svg>
      </button>
    `
    
    flashContainer.appendChild(alertDiv)
  }

  updateUsageStats(emailsUsed, monthlyLimit) {
    console.log("updateUsageStats called with:", emailsUsed, monthlyLimit)
    console.log("Has usageText target:", this.hasUsageTextTarget)
    console.log("Has progressBar target:", this.hasProgressBarTarget)
    
    // Update usage text if target exists
    if (this.hasUsageTextTarget) {
      const remaining = Math.max(monthlyLimit - emailsUsed, 0)
      const newText = `You have ${remaining} pitches remaining this month`
      console.log("Updating usage text to:", newText)
      this.usageTextTarget.textContent = newText
      console.log("Usage text now:", this.usageTextTarget.textContent)
    } else {
      console.log("No usageText target found")
    }
    
    // Update progress bar if target exists
    if (this.hasProgressBarTarget) {
      const percentage = (emailsUsed / monthlyLimit) * 100
      console.log("Updating progress bar to:", percentage + "%")
      this.progressBarTarget.style.width = `${percentage}%`
      console.log("Progress bar width now:", this.progressBarTarget.style.width)
    } else {
      console.log("No progressBar target found")
    }
    
    // Disable submit button if limit reached
    if (emailsUsed >= monthlyLimit && this.hasSubmitButtonTarget) {
      console.log("Limit reached, disabling button")
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }
}
